<template>
  <div class="markdown-with-screenshots" :class="{ 'markdown-with-screenshots--step-guide': stepGuide }" v-html="processedMarkdown"></div>
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, watch, ref } from 'vue';
import MarkdownIt from 'markdown-it';
import Screenshot from '@/storybook/templates/smart_actions/Screenshot.vue';
import { createApp } from 'vue';

interface Props {
  source: string;
  linkify?: boolean;
  typographer?: boolean;
  breaks?: boolean;
  stepGuide?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  linkify: false,
  typographer: false,
  breaks: false,
  stepGuide: false,
});

const md = new MarkdownIt({
  linkify: props.linkify,
  typographer: props.typographer,
  breaks: props.breaks,
  html: true,
});

// Custom renderer for links to handle linked images
const defaultLinkOpenRenderer =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, env, renderer) {
    return renderer.renderToken(tokens, idx, options);
  };

const defaultLinkCloseRenderer =
  md.renderer.rules.link_close ||
  function (tokens, idx, options, env, renderer) {
    return renderer.renderToken(tokens, idx, options);
  };

// Custom renderer for images to create placeholder divs
const defaultImageRenderer =
  md.renderer.rules.image ||
  function (tokens, idx, options, env, renderer) {
    return renderer.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function (tokens, idx, options, env, renderer) {
  const token = tokens[idx];
  const hrefIndex = token.attrIndex('href');

  // Check if the next token is an image (indicating [![alt](src)](href) pattern)
  if (hrefIndex >= 0 && idx + 2 < tokens.length && tokens[idx + 1].type === 'image') {
    const href = token.attrs![hrefIndex][1];
    const imageToken = tokens[idx + 1];
    const srcIndex = imageToken.attrIndex('src');
    const altIndex = imageToken.attrIndex('alt');

    if (srcIndex >= 0) {
      const src = imageToken.attrs![srcIndex][1];
      const alt = altIndex >= 0 ? imageToken.attrs![altIndex][1] : '';

      // Create a unique ID for this screenshot
      const screenshotId = `screenshot-${Math.random().toString(36).substr(2, 9)}`;

      // Store the expanded URL in the environment for the image renderer
      env.screenshotData = env.screenshotData || {};
      env.screenshotData[screenshotId] = { src, alt, expandedSrc: href };

      return `<div class="screenshot-placeholder" data-screenshot-id="${screenshotId}" data-src="${src}" data-alt="${alt}" data-expanded-src="${href}"></div>`;
    }
  }

  return defaultLinkOpenRenderer(tokens, idx, options, env, renderer);
};

md.renderer.rules.image = function (tokens, idx, options, env, renderer) {
  const token = tokens[idx];

  // Check if this image is part of a linked image (skip rendering if so)
  if (idx > 0 && tokens[idx - 1].type === 'link_open') {
    return ''; // Skip rendering, handled by link_open
  }

  const srcIndex = token.attrIndex('src');
  const altIndex = token.attrIndex('alt');

  if (srcIndex >= 0) {
    const src = token.attrs![srcIndex][1];
    const alt = altIndex >= 0 ? token.attrs![altIndex][1] : '';

    // Create a unique ID for this screenshot
    const screenshotId = `screenshot-${Math.random().toString(36).substr(2, 9)}`;

    return `<div class="screenshot-placeholder" data-screenshot-id="${screenshotId}" data-src="${src}" data-alt="${alt}"></div>`;
  }

  return defaultImageRenderer(tokens, idx, options, env, renderer);
};

md.renderer.rules.link_close = function (tokens, idx, options, env, renderer) {
  // Check if this closes a linked image
  if (idx > 1 && tokens[idx - 2].type === 'link_open' && tokens[idx - 1].type === 'image') {
    return ''; // Skip rendering, handled by link_open
  }

  return defaultLinkCloseRenderer(tokens, idx, options, env, renderer);
};

type MarkdownToken = ReturnType<typeof md.parse>[number];

const cloneToken = (token: MarkdownToken, overrides: Partial<MarkdownToken>) => {
  return Object.assign(Object.create(Object.getPrototypeOf(token)), token, overrides) as MarkdownToken;
};

// "**Title.** Body" in one line becomes a title paragraph and a body paragraph, so the
// card shows the title beside the circle and the body below it.
const splitLeadingTitle = (tokens: MarkdownToken[], paragraphIndex: number) => {
  const inline = tokens[paragraphIndex + 1];
  const children = (inline.children || []).filter(child => !(child.type === 'text' && child.content === ''));
  const strongCloseIndex = children.findIndex(child => child.type === 'strong_close');

  if (children[0]?.type !== 'strong_open' || strongCloseIndex < 0) {
    return;
  }

  const bodyChildren = children.slice(strongCloseIndex + 1);
  while (bodyChildren.length > 0 && ['softbreak', 'hardbreak'].includes(bodyChildren[0].type)) {
    bodyChildren.shift();
  }
  if (bodyChildren[0]?.type === 'text') {
    bodyChildren[0] = cloneToken(bodyChildren[0], { content: bodyChildren[0].content.trimStart() });
  }
  if (bodyChildren.every(child => child.type === 'text' && child.content.trim() === '')) {
    return;
  }

  const paragraphOpen = tokens[paragraphIndex];
  const paragraphClose = tokens[paragraphIndex + 2];
  tokens.splice(
    paragraphIndex + 1,
    1,
    cloneToken(inline, { children: children.slice(0, strongCloseIndex + 1) }),
    cloneToken(paragraphClose, {}),
    cloneToken(paragraphOpen, {}),
    cloneToken(inline, { children: bodyChildren })
  );
};

const processedMarkdown = computed(() => {
  const env: { stepGuide?: boolean } = { stepGuide: props.stepGuide };
  const tokens = md.parse(props.source, env);

  if (props.stepGuide) {
    let stepNumber = 1;
    let stepListLevel: number | undefined;
    let isFirstStepParagraph = false;

    for (let index = 0; index < tokens.length; index += 1) {
      const token = tokens[index];

      if (token.type === 'ordered_list_open' && token.level === 0) {
        stepListLevel = token.level;
        stepNumber = Number(token.attrGet('start') || 1);
        token.attrJoin('class', 'step-guide');
      } else if (token.type === 'list_item_open' && stepListLevel === 0 && token.level === 1) {
        token.attrJoin('class', 'step-guide__step');
        token.attrSet('data-step-number', String(stepNumber));
        stepNumber += 1;
        isFirstStepParagraph = true;
      } else if (['paragraph_open', 'paragraph_close'].includes(token.type) && stepListLevel === 0 && token.level === 2) {
        // A tight list hides its paragraphs, which leaves bare text as separate grid cells in the card.
        token.hidden = false;
        if (token.type === 'paragraph_open' && isFirstStepParagraph) {
          isFirstStepParagraph = false;
          splitLeadingTitle(tokens, index);
        }
      } else if (token.type === 'ordered_list_close' && token.level === stepListLevel) {
        stepListLevel = undefined;
      }
    }
  }

  return md.renderer.render(tokens, md.options, env);
});

const mountedApps = ref<any[]>([]);

const mountScreenshots = () => {
  nextTick(() => {
    // Clean up existing apps
    mountedApps.value.forEach(app => {
      try {
        app.unmount();
      } catch {
        // Ignore unmount errors
      }
    });
    mountedApps.value = [];

    // Find all screenshot placeholders and mount Screenshot components
    const placeholders = document.querySelectorAll('.screenshot-placeholder');

    placeholders.forEach(placeholder => {
      const src = placeholder.getAttribute('data-src');
      const alt = placeholder.getAttribute('data-alt') || '';
      const expandedSrc = placeholder.getAttribute('data-expanded-src');

      if (src && placeholder instanceof HTMLElement) {
        // Create Vue app for this Screenshot component
        const app = createApp(Screenshot, {
          src: src,
          expandedSrc: expandedSrc || src, // Use expandedSrc if available, otherwise fallback to src
          alt: alt,
        });

        // Clear the placeholder content and mount the component
        placeholder.innerHTML = '';
        app.mount(placeholder);
        mountedApps.value.push(app);
      }
    });
  });
};

onMounted(mountScreenshots);
watch(() => props.source, mountScreenshots);
</script>

<style scoped>
.markdown-with-screenshots {
  display: flex;
  flex-direction: column;
}

.markdown-with-screenshots :deep(h1),
.markdown-with-screenshots :deep(h2),
.markdown-with-screenshots :deep(h3),
.markdown-with-screenshots :deep(h4),
.markdown-with-screenshots :deep(h5),
.markdown-with-screenshots :deep(h6) {
  font-weight: bold;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
}

.markdown-with-screenshots :deep(h1:first-child),
.markdown-with-screenshots :deep(h2:first-child),
.markdown-with-screenshots :deep(h3:first-child),
.markdown-with-screenshots :deep(h4:first-child),
.markdown-with-screenshots :deep(h5:first-child),
.markdown-with-screenshots :deep(h6:first-child) {
  margin-top: 0;
}

.markdown-with-screenshots :deep(h1) {
  font-size: 1.5rem;
}
.markdown-with-screenshots :deep(h2) {
  font-size: 1.25rem;
}
.markdown-with-screenshots :deep(h3) {
  font-size: 1.125rem;
}

.markdown-with-screenshots :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-with-screenshots :deep(ul),
.markdown-with-screenshots :deep(ol) {
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
}

.markdown-with-screenshots :deep(li) {
  margin-bottom: 0.25rem;
}

.markdown-with-screenshots :deep(blockquote) {
  border-left: 4px solid #d1d5db;
  padding-left: 1rem;
  font-style: italic;
  margin-bottom: 0.5rem;
}

.markdown-with-screenshots :deep(code) {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.markdown-with-screenshots :deep(pre) {
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  overflow-x: auto;
}

.dark .markdown-with-screenshots :deep(code),
.dark .markdown-with-screenshots :deep(pre) {
  background-color: #374151;
}

.markdown-with-screenshots :deep(.screenshot-placeholder) {
  margin: 0.5rem 0;
}

/* The step guide prompt caps headings at h4, which would otherwise render at body size. */
.markdown-with-screenshots--step-guide :deep(:is(h4, h5, h6)) {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.markdown-with-screenshots :deep(.step-guide) {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step) {
  display: grid;
  grid-template-columns: 2.25rem minmax(0, 1fr);
  column-gap: 1rem;
  row-gap: 0.25rem;
  margin: 0;
  padding: 1rem;
  border: 1px solid #e1e8f0;
  border-radius: 10px;
  background: #fff;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step::before) {
  display: flex;
  grid-column: 1;
  grid-row: 1;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #5a3d7a, #342653);
  color: #fff;
  content: attr(data-step-number);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step > *) {
  grid-column: 2;
  min-width: 0;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step > p:first-child) {
  align-self: center;
  margin: 0;
  color: #1c232b;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.25rem;
  letter-spacing: -0.02em;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step > ul),
.markdown-with-screenshots :deep(.step-guide > .step-guide__step > ol) {
  margin: 0;
  padding-left: 1.25rem;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step > ul) {
  list-style: disc;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step > ol) {
  list-style: decimal;
}

.dark .markdown-with-screenshots :deep(.step-guide > .step-guide__step) {
  border-color: #3f3f46;
  background: #27272a;
}

.dark .markdown-with-screenshots :deep(.step-guide > .step-guide__step > p:first-child) {
  color: #f4f4f5;
}

.markdown-with-screenshots :deep(.step-guide > .step-guide__step > .screenshot-placeholder) {
  grid-column: 1 / -1;
  margin: 0.75rem 0 0;
}

/* Pulls a screenshot under a bullet back to the card edge: 2.25rem circle + 1rem gap + 1.25rem list padding. */
.markdown-with-screenshots :deep(.step-guide > .step-guide__step > ul > li > .screenshot-placeholder),
.markdown-with-screenshots :deep(.step-guide > .step-guide__step > ol > li > .screenshot-placeholder) {
  margin: 0.75rem 0 0 -4.5rem;
}
</style>
